import classnames from 'classnames';
import styles from './FJVerticalScroller.module.less';
import React, { useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { debounce } from 'lodash-es';

export interface FJVerticalScrollerRef {
    scrollTop: (top: number, behavior?: ScrollBehavior) => void;
    scrollToBottom: () => void;
    getScroller: () => HTMLDivElement | null;
}

interface FJVerticalScrollerProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    onScroll?: () => void;
    onScrollStart?: () => void;
    onTotalScroll?: () => void;
    onMouseWheel?: (event: React.WheelEvent<HTMLDivElement>) => void;
    totalScrollThreshold?: number;
    children: React.ReactNode;
}

const FJVerticalScroller: React.ForwardRefRenderFunction<FJVerticalScrollerRef, FJVerticalScrollerProps> = (
    { className, onScroll, onScrollStart, onTotalScroll, totalScrollThreshold = 50, children, ...props },
    ref,
) => {
    const _beforeScrollerTop = useRef(0);
    const scrollerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            scrollTop: (top: number, behavior: ScrollBehavior = 'auto') => {
                if (!scrollerRef.current) return;
                scrollerRef.current.scroll({ top, behavior });
            },
            scrollToBottom: () => {
                if (!scrollerRef.current) return;
                scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
            },
            getScroller: () => scrollerRef.current || null,
        }),
        [],
    );

    const onScrollDebounce = useMemo(
        () =>{
            return onTotalScroll
                ? debounce(
                    // eslint-disable-next-line react-hooks/refs
                    (scroller: HTMLDivElement) => {
                          if (!scroller) return;
                          const afterScrollTop = scroller.scrollTop;
                          const isScrollingDown = afterScrollTop > _beforeScrollerTop.current;

                          if (
                              isScrollingDown &&
                              scroller.scrollTop + scroller.offsetHeight >= scroller.scrollHeight - totalScrollThreshold
                          ) {
                              onTotalScroll();
                          }
                          _beforeScrollerTop.current = afterScrollTop;
                      },
                      200,
                      { trailing: true },
                  )
                : undefined;
        },
        [onTotalScroll, totalScrollThreshold],
    );

    useEffect(() => {
        const scrollerElement = scrollerRef.current;
        if (!scrollerElement) return;

        const scroll = () => {
            onScroll?.();
            onScrollStart?.();
            onScrollDebounce?.(scrollerElement);
        };

        scrollerElement.addEventListener('scroll', scroll);
        return () => {
            scrollerElement.removeEventListener('scroll', scroll);
        };
    }, [onScroll, onScrollStart, onScrollDebounce]);

    const cn = classnames(styles.scroller_vertical, className);

    return (
        <div className={cn} ref={scrollerRef} {...props}>
            {children}
        </div>
    );
};

export default forwardRef(FJVerticalScroller);
